import React, { useEffect } from "react";

import classnames from "classnames";
import isIntuit from "Intuit/isIntuit";
import { connect } from "react-redux";
import { Field, change, autofill, touch, type Validator } from "redux-form";

import BodyText from "Common/BodyText";
import { setFieldValid } from "Common/forms/Actions";
import SIFormInput from "Common/forms/SIFormInput";
import SIFormLabel from "Common/forms/SIFormLabel";
import SIFormRadioGroup from "Common/forms/SIFormRadioGroup";
import { Col } from "Common/Grid";
import { Div } from "Common/HTML";

import type { Dispatch } from "redux";

import makeBem from "Utils/makeBem";

const bem = makeBem("SIFormField");
type Props<T extends React.ElementType> = {
  /**
  The key you want the form value to be under when submitting.
  * */
  name?: string;

  /**
  The type of input field you want to use. (e.g Radio, Dropdown, etc.). Defaults to a simple text field.
  * */
  component: T;

  /**
  @ignore
  * */
  props?: Partial<React.ElementProps<T>>;
  className?: string;

  /**
  The label on top of the field you want to display to the user.
  * */
  label?: string;

  /**
  Flag for disabling the form field
  * */
  disabled?: boolean;

  /**
  Flag for placing label in same line as input.
  * */
  inlineLabel: boolean;

  /**
  Small muted inline text for basic helpers, sits next to label.
  * */
  hint?: string;

  /**
  An info popover that provides more than the basic
  * */
  guidance?: string;

  /**
  The text shown that triggers the info popover message being shown
  * */
  triggerText?: string;

  /**
  @ignore
  * */
  strongGuidance?: string;

  /**
  Flag for ensuring errors whitespace is reserved so that form doesn't appear to bounce
  * */
  noBounce?: boolean;
  compact?: boolean;
  id?: string;

  /**
  @ignore Passed in by Field Component
  * */
  meta: {
    touched: boolean;
    error?: any;
    warning?: any;
    valid: boolean;
    autofilled: boolean;
  };
  input: {
    name: string;
  };
  setFieldValid: () => {};
};

/**
 * Flex container for form fields
 * Inline fields are wrapped so flex properties are scoped to this input, not the parent
 * Other fields are rendered as-is
 * */
const FieldContainer = ({ inlineLabel, children = null, ...props }: { inlineLabel: boolean; children?: React.ReactNode }) =>
  inlineLabel ? (
    <Div {...props} className="flex justify-start flex-row">
      {children}
    </Div>
  ) : (
    children
  );

// SIFormField is a full-width horizontal form field. It is responsive.
export class SIFormFieldDumb extends React.Component<Props<React.ElementType>> {
  static defaultProps = {
    component: SIFormInput,
    inlineLabel: false,
    noBounce: false,
    compact: false,
    meta: {
      touched: false,
      error: false,
      warning: false,
      valid: false,
      autofilled: false,
    },
  };

  componentDidMount() {
    const { setFieldValid } = this.props;
    if (setFieldValid) setFieldValid();
  }

  componentDidUpdate() {
    const { setFieldValid } = this.props;
    if (setFieldValid) setFieldValid();
  }

  validationText() {
    const usedMetaVals = {
      error: this.props.meta.error,
      warning: this.props.meta.warning,
      untouched: null,
      valid: null,
    };
    const status = this.status();
    let text = usedMetaVals[status];

    if (isIntuit() && text === "Required") {
      text = null;
    }

    return text;
  }

  status() {
    const { touched, error, warning, autofilled } = this.props.meta;
    let returnVal = "valid";
    const autoFillError = error && autofilled;

    if (!touched && autoFillError) {
      returnVal = "error";
    } else if (!touched) {
      returnVal = "untouched";
    } else if (error) {
      returnVal = "error";
    } else if (warning) {
      returnVal = "warning";
    }

    return returnVal;
  }

  render() {
    const {
      component,
      props: componentProps,
      className,
      input,
      name,
      label,
      inlineLabel,
      hint,
      guidance,
      triggerText,
      strongGuidance,
      noBounce,
      compact,
      id,
      meta,
      setFieldValid: _setFieldValid,
      ...rest
    } = this.props;
    const inputId = id || (input && input.name) || name;

    return (
      <FieldContainer inlineLabel={inlineLabel}>
        {label && (
          <Col
            className={classnames(className, { "text-xs-right": inlineLabel })}
            {...(inlineLabel
              ? {
                  xs: 5,
                }
              : {})}
          >
            <SIFormLabel
              status={this.status()}
              label={label}
              inlineLabel={inlineLabel}
              hint={hint}
              guidance={guidance}
              triggerText={triggerText}
              htmlFor={inputId}
              {...(inlineLabel && component !== SIFormRadioGroup
                ? {
                    mt: 3,
                  }
                : {})}
            />
          </Col>
        )}
        <Col className={classnames(className, bem())} xs={12} mb={isIntuit() ? 3 : compact ? 0 : 4} {...rest}>
          {React.createElement(component, {
            id: inputId,
            input,
            meta,
            ...componentProps,
          })}
          <BodyText
            className={classnames(bem("Validation"), bem("Validation", this.status()), noBounce ? bem("Validation", "noBounce") : "")}
            error={this.status() === "error"}
            mt={2}
          >
            {this.validationText()}
          </BodyText>
          {strongGuidance && <Div pt={2}>{strongGuidance}</Div>}
        </Col>
      </FieldContainer>
    );
  }
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: Props<any>) => ({
  setFieldValid: () => dispatch(setFieldValid(ownProps.input.name, ownProps.meta.valid)),
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);
const SIFormFieldWithRedux = withRedux(SIFormFieldDumb);
type SIFormFieldProps<T extends React.ElementType> = {
  component?: T;
  props?: Partial<React.ElementProps<T>>;
  setTouched: (arg0: string) => (...args: Array<any>) => any;
  touchOnChangeFormName?: string;
  prefilledValue?: {
    formName: string;
    fieldName: string;
    initial: boolean;
    value: string | number | boolean;
  } | null;
  name: string;
  validate?: Validator | Validator[];
  dispatch: (...args: Array<any>) => any;
} & React.ElementProps<typeof Field>;

const SIFormField = <T extends React.ElementType>({
  component,
  props,
  setTouched,
  touchOnChangeFormName,
  prefilledValue,
  dispatch,
  validate,
  parse,
  format,
  ...rest
}: SIFormFieldProps<T>) => {
  useEffect(() => {
    if (prefilledValue) {
      const { formName, fieldName, value, initial } = prefilledValue;

      if (initial) {
        dispatch(change(formName, fieldName, value));
      } else {
        dispatch(autofill(formName, fieldName, value));
      }
    }
  }, [prefilledValue]);
  return (
    <Field
      validate={validate}
      parse={parse}
      format={format}
      component={SIFormFieldWithRedux as (...args: Array<any>) => any}
      props={{
        component,
        props,
      }}
      {...(touchOnChangeFormName
        ? {
            onChange: setTouched(touchOnChangeFormName),
          }
        : {})}
      {...rest}
    />
  );
};

const outerWrapperDispatch = (dispatch: Dispatch<any>) => ({
  setTouched: (formName) => (_event, _newValue, _previousValue, inputName) => dispatch(touch(formName, inputName)),
  dispatch,
});

const withOuterWrapperRedux = connect(null, outerWrapperDispatch);
export default withOuterWrapperRedux(SIFormField);
